import { Injectable } from '@angular/core';
import { SideMenuItem } from '@flux/design-system';
import { FeatureFlagsEnum } from '@generated/featureFlags.enum';
import { GlobalPermissionsEnum, PermissionsEnum } from '@generated/permissions.enum';

/**
 * Extended SideMenuItem with permission and feature flag support
 */
export interface NavigationMenuItem extends Omit<SideMenuItem, 'hidden' | 'children'> {
  children?: NavigationMenuItem[];
  has_feature_flag?: `${FeatureFlagsEnum}`[] | FeatureFlagsEnum[];
  required_permissions?: GlobalPermissionsEnum[] | `${GlobalPermissionsEnum}`[];
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private navigations: NavigationMenuItem[] = [
    {
      id: 'meu-espaco',
      title: 'Meu espaço',
      url: 'meu-espaco',
      icon: { unselected: 'home-outline', selected: 'home-filled' },
    },
    {
      id: 'fluxos',
      title: 'Fluxos de trabalho',
      url: 'fluxos',
      icon: { unselected: 'view-kanban-outline', selected: 'view-kanban-filled' },
      required_permissions: [
        PermissionsEnum.GlobalPipesReadAll,
        PermissionsEnum.GlobalPipesReadLimited,
      ],
    },

    {
      id: 'banco-de-dados',
      title: 'Banco de dados',
      url: 'banco-de-dados',
      icon: { unselected: 'database-outline', selected: 'database-filled' },
      has_feature_flag: [FeatureFlagsEnum.FeatureDatabase],
      required_permissions: [
        PermissionsEnum.GlobalDatabaseReadAll,
        PermissionsEnum.GlobalDatabaseReadLimited,
      ],
    },
    {
      id: 'configuracoes',
      title: 'Configurações',
      withDivider: true,
      children: [
        {
          id: 'gerenciar-papeis',
          title: 'Papéis',
          url: 'gerenciar/papeis',
          icon: { unselected: 'shield-outline', selected: 'shield-filled' },
          withDivider: true,
          required_permissions: [PermissionsEnum.GlobalRolesManagePermissions],
        },
        {
          id: 'gerenciar-pessoas',
          title: 'Pessoas',
          url: 'gerenciar/pessoas',
          icon: { unselected: 'peoples-outline', selected: 'peoples-filled' },
          required_permissions: [PermissionsEnum.GlobalRolesManageUsers],
        },
        {
          id: 'gerenciar-times',
          title: 'Times',
          url: 'gerenciar/times',
          has_feature_flag: [FeatureFlagsEnum.FeatureTeams],
          icon: { unselected: 'peoples-outline', selected: 'peoples-filled' },
          required_permissions: [PermissionsEnum.GlobalTeamsManageUsers],
        },
        {
          id: 'gerenciar-variaveis',
          title: 'Variáveis customizadas',
          url: 'gerenciar/variaveis',
          icon: { unselected: 'code-outline', selected: 'code-outline' },
          has_feature_flag: [FeatureFlagsEnum.FeatureCustomVariables],
          required_permissions: [PermissionsEnum.GlobalVariablesRead],
        },
        {
          id: 'gerenciar-contas-de-servico',
          title: 'Contas de serviço',
          url: 'gerenciar/contas-de-servico',
          icon: { unselected: 'robot-outline', selected: 'robot-filled' },
          has_feature_flag: [FeatureFlagsEnum.FeatureServiceAccounts],
          required_permissions: [PermissionsEnum.GlobalServiceAccountsRead],
        },
        {
          id: 'ausencias',
          title: 'Ausências',
          url: 'ausencias',
          icon: { unselected: 'partner-exchange-outline', selected: 'partner-exchange-filled' },
          has_feature_flag: [FeatureFlagsEnum.UserDelegations],
        },
        {
          id: 'gerenciar-faturamento',
          title: 'Faturamento',
          url: 'gerenciar/faturamento',
          icon: { unselected: 'finance-outline', selected: 'finance-filled' },
          required_permissions: [PermissionsEnum.GlobalFeaturesManage],
        },
      ],
      icon: { unselected: 'settings-outline', selected: 'settings-filled' },
    },
  ];

  getNavigationItems(): NavigationMenuItem[] {
    return this.navigations;
  }
}
